import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useParams, useRouteMatch } from "react-router-dom";
import MaterialTable from "material-table";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../../../utilities/constants";
import {
  getDoctorsForDeptRequest,
  removeDoctorFromDeptRequest,
  toggleDoctorHeadRequest,
} from "../../DoctorApiActions";
import ConfirmationDialog from "../../../../utilities/components/ConfirmationDialog/ConfirmationDialog";
import { useStyles } from "./styles";
import defaultUser from "../../../../assets/user.png";
import { debounce } from "../../../../utilities/utilityFunctions";
import ImageLoader from "react-load-image";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import {
  Box,
  Button,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ActiveStatusIcon from "../../../../utilities/components/ActiveStatusIcon/ActiveStatusIcon";

/**
 * function to get message for remove dialog
 * @param doctor: doctor details
 * @param classes: classes from css
 * @returns {JSX.Element}
 * @constructor
 */
const RemoveDialogMessage = ({ doctor, classes }) => {
  return (
    <>
      {doctor && (
        <Box>
          Are you sure you want to remove the{" "}
          <span className={classes.dialogMessageLabel}>
            {doctor.firstName} {doctor.lastName}
          </span>{" "}
          from doctor list?
        </Box>
      )}
    </>
  );
};

/**
 * function to render department doctors list
 * @param getDoctorsForDept: function to get doctors of department
 * @returns {JSX.Element}
 * @constructor
 */
const DepartmentDoctors = ({
  getDoctorsForDept,
  doctors,
  removeDoctor,
  toggleHead,
  orgIdHistory,
  orgNameHistory,
  deptIdHistory,
  deptNameHistory,
}) => {
  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch();
  const [openRemoveConfirmationDialog, setOpenRemoveConfirmationDialog] =
    useState(false);
  const [doctor, setDoctor] = useState(null);
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: {
      name: "",
      email: "",
      phone: "",
      active: "",
    },
  });
  const [isFilterChange, setIsFilterChange] = useState(false);

  /**
   * function to handle table filter search
   * @param filters: filters from filter input to search
   */
  const onTableFilterChange = (filters) => {
    let searchDataOnFilterChange = Object.assign({}, paginationParams);

    if (filters.length > 0) {
      let obj = {};
      filters.forEach((filter) => {
        obj[filter.column.field] = filter.value;
      });

      Object.keys(searchDataOnFilterChange.searchData).forEach(function (key) {
        if (key !== "active") {
          if (obj[key]) {
            searchDataOnFilterChange.searchData[key] = obj[key];
          } else {
            searchDataOnFilterChange.searchData[key] = "";
          }
        }
      });

      filters.forEach((filter) => {
        searchDataOnFilterChange.searchData[filter.column.field] = filter.value;
      });
    } else {
      searchDataOnFilterChange.searchData["name"] = "";
      searchDataOnFilterChange.searchData["email"] = "";
      searchDataOnFilterChange.searchData["phone"] = "";
    }

    setIsFilterChange(true);
    setPaginationParams(searchDataOnFilterChange);
  };

  /**
   * function to implement debounce
   * @type {(function(...[*]=): void)|*}
   */
  //eslint-disable-next-line
  const debounceOnFilterChange = React.useCallback(
    debounce(onTableFilterChange, 400),
    []
  );

  useEffect(() => {
    getDoctorsForDept(params.id, paginationParams, isFilterChange).then(
      (res) => {
        if (isFilterChange) {
          setIsFilterChange(false);
        }
      }
    );
    //eslint-disable-next-line
  }, [params.id, paginationParams, getDoctorsForDept]);

  /**
   * function to handle status change upon filter
   * @param value: selected status value
   */
  const handleStatusChange = (value) => {
    let data = Object.assign({}, paginationParams);
    data.searchData.active = value;

    setPaginationParams(data);
  };

  /**
   * function to toggle change for head on switch change
   * @param props: doctor details
   */
  const handleChange = (props) => {
    const data = {
      teamId: Number(params.id),
      headDoctor: !props.headDoctor,
    };

    toggleHead(props.userId, data).then((res) => {
      getDoctorsForDept(params.id, paginationParams).then((res) => {});
    });
  };

  /**
   * function to remove doctor on button click
   */
  const onRemove = () => {
    const data = {
      caregiverId: doctor.userId,
    };
    removeDoctor(params.id, data).then((res) => {
      setOpenRemoveConfirmationDialog(false);
      getDoctorsForDept(params.id, paginationParams).then((res) => {});
    });
  };

  const columns = [
    {
      field: "name",
      title: "Name",
      filtering: true,
      defaultFilter: paginationParams.searchData["name"],
      cellStyle: {
        width: "40%",
      },
      headerStyle: {
        width: "40%",
      },
      render: ({ imageUrl, name, userId }) => {
        return (
          <Box
            className={classes.profileContainer}
            component={Link}
            to={{
              pathname: `${match.path.split(":")[0]}doctors/${userId}`,
              state: {
                orgIdHistory: orgIdHistory,
                orgNameHistory: orgNameHistory,
                deptIdHistory: deptIdHistory,
                deptNameHistory: deptNameHistory,
              },
            }}
          >
            <Box className={classes.profileInnerContainer}>
              <ImageLoader src={imageUrl ? imageUrl : defaultUser}>
                <img alt={userId} className={classes.profileImage} />
                <div className={classes.profileImageError}>{userId}</div>
                <Preloader alt={userId} className={classes.profileImage} />
              </ImageLoader>
            </Box>

            <Box className={classes.nameContainer}>
              <Typography className={classes.name}>{name}</Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "email",
      title: "Email",
      filtering: true,
      defaultFilter: paginationParams.searchData["email"],
      cellStyle: {
        width: "20%",
      },
      headerStyle: {
        width: "20%",
      },
    },
    {
      field: "phone",
      title: "Phone",
      filtering: true,
      defaultFilter: paginationParams.searchData["phone"],
      cellStyle: {
        width: "20%",
      },
      headerStyle: {
        width: "20%",
      },
    },
    {
      field: "role",
      title: "Role",
      filtering: false,

      cellStyle: {
        width: "auto",
      },
      headerStyle: {
        width: "auto",
      },
    },
    {
      field: "headDoctor",
      title: "Head Doctor",
      filtering: false,

      cellStyle: {
        textAlign: "center",
        width: "15%",
      },
      headerStyle: {
        textAlign: "center",
        width: "15%",
      },
      render: (props) => {
        return (
          <>
            {props.role !== "Healthcare Professional" &&
              props.role !== "Org Admin" && (
                <Switch
                  checked={props.headDoctor === true}
                  onClick={() => handleChange(props)}
                />
              )}
          </>
        );
      },
    },
    {
      field: "casesCount",
      title: "Cases",
      filtering: false,
      cellStyle: {
        textAlign: "center",
        width: "10%",
      },
      headerStyle: {
        textAlign: "center",
        width: "10%",
      },
    },
    {
      field: "enabled",
      title: "Active Status",
      cellStyle: {
        width: "10%",
        textAlign: "center",
      },
      headerStyle: {
        width: "10%",
        textAlign: "center",
      },
      filtering: true,
      filterComponent: () => {
        return (
          <Box>
            <FormControl fullWidth>
              <Select
                value={paginationParams.searchData.active}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      },
      render: ({ enabled, isBanned }) => {
        return (
          <ActiveStatusIcon
            enabled={enabled}
            isBanned={isBanned}
            isLabel={false}
          />
        );
      },
    },
    {
      field: "delete",
      title: "Remove",
      cellStyle: {
        width: "10%",
        textAlign: "center",
      },
      headerStyle: {
        width: "10%",
        textAlign: "center",
      },
      filtering: false,
      render: (props) => {
        return (
          <>
            <Tooltip arrow placement="top" title="Remove">
              <Icon
                className={classes.removeIcon}
                onClick={() => {
                  setOpenRemoveConfirmationDialog(true);
                  setDoctor(props);
                }}
              >
                highlight_off
              </Icon>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Box className={classes.addButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Icon>add</Icon>}
          disableElevation={true}
          component={Link}
          to={{
            pathname: `${match.path.split(":")[0]}addDoctor`,
            state: {
              orgIdHistory: orgIdHistory,
              orgNameHistory: orgNameHistory,
              deptIdHistory: deptIdHistory,
              deptNameHistory: deptNameHistory,
            },
          }}
          classes={{ root: classes.addButton }}
        >
          Staff
        </Button>
      </Box>

      <MaterialTable
        title={""}
        columns={columns}
        data={doctors.rows}
        options={{
          search: false,
          filtering: true,
          sorting: false,
          draggable: false,
          horizontalScroll: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          pageSize: paginationParams.rowsPerPage,
          emptyRowsWhenPaging: false,
        }}
        pages={Math.ceil(doctors.count / paginationParams.rowsPerPage)}
        totalCount={doctors.count}
        page={paginationParams.page}
        onChangeRowsPerPage={(pageSize) => {
          let data = Object.assign({}, paginationParams);
          data["rowsPerPage"] = pageSize;
          setPaginationParams(data);
        }}
        onChangePage={(page) => {
          let data = Object.assign({}, paginationParams);
          data["page"] = page;
          setPaginationParams(data);
        }}
        onFilterChange={(filters) => {
          debounceOnFilterChange(filters);
        }}
      />

      {openRemoveConfirmationDialog && doctor && (
        <Box>
          <ConfirmationDialog
            title="Remove"
            message={<RemoveDialogMessage doctor={doctor} classes={classes} />}
            open={openRemoveConfirmationDialog}
            setOpen={setOpenRemoveConfirmationDialog}
            onConfirm={onRemove}
          />
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    doctors: state.doctors.doctors,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getDoctorsForDept: (deptId, data, isHideLoader = false) => {
      return dispatch(
        getDoctorsForDeptRequest(deptId, data, isHideLoader)
      ).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    removeDoctor: (deptId, data) => {
      return dispatch(removeDoctorFromDeptRequest(deptId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    toggleHead: (doctorId, data) => {
      return dispatch(toggleDoctorHeadRequest(doctorId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentDoctors);
